import { Formik } from 'formik';
import * as Yup from 'yup';
import React, { useEffect, useState } from 'react';
import TextArea from 'components/form/TextArea';
import Button from 'components/form/Button';
import { useDispatch, useSelector } from 'react-redux';
import { decrypt, encrypt, objectToFormData } from 'utils/helpers';
import { editEvent, getEvent, showSuccess } from 'store/slices';
import { useParams } from 'react-router-dom';
import Loader from 'components/Layout/Loader';
import usePermission from 'hooks/usePermission';

const EventAcceptanceLetterheadForm = ({
  eventId,
  eventTab = false,
  handleNext,
  isTemp,
}) => {
  const dispatch = useDispatch();
  const { viewAccess, createAccess, updateAccess, deleteAccess } = usePermission('Event Management');

  const { eventData } = useSelector((state) => ({
    eventData: state.global.eventData,
  }));

  const [btnLoading, setBtnLoading] = useState(false);
  const [showPreview, setShowPreview] = useState('');

  const isEdit = eventData?.acceptance_letter_html;
  const previewLetterhead = decrypt(eventData?.acceptance_letter_html || '');

  const initialValues = { htmlLetter: isEdit ? previewLetterhead : '' };
  const validationSchema = Yup.object().shape({
    htmlLetter: Yup.string().required('HTML code is required.'),
  });
  const [isDataSaved, setIsDataSaved] = useState(false);
  const [isFormLoading, setIsFormLoading] = useState(true);
  const params = useParams();

  const fetchEventDetails = async () => {
    const temp_id = isTemp ? `&temp_id=${isTemp}` : '';
    await dispatch(getEvent(params?.eventId, temp_id));
    setIsFormLoading(false);
  };

  const handleSave = async (values, shouldProceedNext = false) => {
    setBtnLoading(true);
    setIsDataSaved(true);

    const payloadData = {
      html_letter: encrypt(values?.htmlLetter),
      type: 6,
      id: eventId,
      ...(isTemp ? { is_temp: true } : {}),
    };
    const payload = objectToFormData(payloadData);
    const response = await dispatch(editEvent(payload));
    if (response?.status === 200) {
      fetchEventDetails();
      if (shouldProceedNext) {
        handleNext('acceptance_letterhead');
      } else {
        dispatch(showSuccess(`Acceptance format uploaded successfully.`));
      }
    }
    setBtnLoading(false);
  };

  useEffect(() => {
    fetchEventDetails();
  }, []);


  return (
    <div className="acceptance-letterhead-container row">
      {isFormLoading ? (
        <div className="cmb-24 cps-30 cpe-30 cpt-40 cpb-40 d-flex align-items-center justify-content-center">
          <Loader size="md" />
        </div>
      ) : (
        <>
          <Formik
            initialValues={initialValues}
            onSubmit={handleSave}
            validationSchema={validationSchema}
            enableReinitialize
          >
            {(props) => {
              const { values, errors, handleChange, handleSubmit, resetForm } =
                props;
              return (
                <form className="col-md-6">
                  <div className="cmb-15">
                    <TextArea
                      label={'HTML Content'}
                      isRequired
                      id="htmlLetter"
                      value={values?.htmlLetter}
                      onChange={handleChange}
                      error={errors?.htmlLetter}
                      rows={10}
                      placeholder="Write your HTML code here..."
                    />
                  </div>
                  {eventTab ? (
                    <div className="d-flex flex-wrap gap-2">
                      <div className="flex-grow-1 flex-sm-grow-0">
                        <Button
                          text={'Preview'}
                          btnStyle={'primary-dark'}
                          className="w-100 w-sm-auto cps-20 cpe-20 h-37"
                          disabled={!values?.htmlLetter}
                          onClick={() => setShowPreview(values?.htmlLetter)}
                        />
                      </div>
                      {(updateAccess === '1' || createAccess === '1') && (
                        <div className="flex-grow-1 flex-sm-grow-0">
                          <Button
                            text={'Save'}
                            btnStyle={'primary-dark'}
                            className="w-100 w-sm-auto cps-20 cpe-20 h-37"
                            onClick={handleSubmit}
                            disabled={!showPreview || btnLoading}
                            btnLoading={btnLoading}
                          />
                        </div>
                      )}
                      <div className="flex-grow-1 flex-sm-grow-0">
                        <Button
                          text={'Reset'}
                          btnStyle="light-outline"
                          className="w-100 w-sm-auto cps-20 cpe-20 h-37"
                          onClick={() => {
                            resetForm();
                            setShowPreview('');
                          }}
                        />
                      </div>
                      {(updateAccess === '1' || createAccess === '1') && (
                        <div className="flex-grow-1 flex-sm-grow-0">
                          <Button
                            text={
                              isDataSaved || !showPreview
                                ? 'Proceed Next'
                                : 'Proceed Next & Save'
                            }
                            btnStyle="light-outline"
                            className={`w-100 w-sm-auto h-37 ${isDataSaved || !showPreview
                                ? 'cps-20 cpe-20'
                                : 'cps-10 cpe-10'
                              }`}
                            disabled={btnLoading}
                            onClick={() => {
                              if (isDataSaved || !showPreview) {
                                handleNext('acceptance_letterhead');
                              } else {
                                handleSave(values, true);
                              }
                            }}
                          />
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="d-flex gap-2">
                      <Button
                        isRounded
                        text={'Preview'}
                        btnStyle={'primary-dark'}
                        className={'cps-20 cpe-20 h-35'}
                        disabled={!values?.htmlLetter}
                        onClick={() => {
                          setShowPreview(values?.htmlLetter);
                        }}
                      />

                      <Button
                        isRounded
                        text={'Save'}
                        btnStyle={'primary-dark'}
                        className={'cps-20 cpe-20 h-35'}
                        onClick={handleSubmit}
                        disabled={!showPreview}
                        btnLoading={btnLoading}
                      />

                      <Button
                        isRounded
                        text={'Reset'}
                        btnStyle="light-outline"
                        className={'cps-20 cpe-20 h-35'}
                        onClick={() => {
                          resetForm();
                          setShowPreview('');
                        }}
                      />
                    </div>
                  )}
                </form>
              );
            }}
          </Formik>
          <div className={`cpt-20 col-md-6 cmb-15`}>
            {isEdit ? (
              <div
                style={{
                  maxHeight: '700px',
                  overflowY: 'auto',
                  border: '1px solid #ccc',
                  padding: '10px',
                  backgroundColor: '#f8f9fa',
                }}
                dangerouslySetInnerHTML={{
                  __html: previewLetterhead,
                }}
              />
            ) : showPreview ? (
              <div
                style={{
                  maxHeight: '700px',
                  overflowY: 'auto',
                  border: '1px solid #ccc',
                  padding: '10px',
                  backgroundColor: '#f8f9fa',
                }}
                dangerouslySetInnerHTML={{
                  __html: showPreview,
                }}
              />
            ) : (
              <div
                className="d-flex align-items-center justify-content-center"
                style={{
                  maxHeight: '500px',
                  height: '225px',
                  overflowY: 'auto',
                  border: '1px solid #ccc',
                  backgroundColor: '#f8f9fa',
                }}
              >
                No preview available
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default EventAcceptanceLetterheadForm;
